import React, { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";

import { crmPostventaIpcApi } from "./crmPostventaIpcApi";
import { codeOf } from "../crmpostventa-shared/crmFormat";
import { CRM_PATHS, gestionHref, isPlainClick, readFichaEditParams } from "../crmpostventa-shared/crmLinks";
import { TIPO_LABELS, motivoLabel } from "./ipcFormat";

// Gestión context of the Ficha opened from "Editar datos" of a gestión (Prompt 7, point 2; T5): the Ficha link carries
// ?cliente=<IdAlta>&gestion=<id>&editar=1 (crmLinks.js). The Ficha keeps its own editor and save (old CRM, D22); this
// module only adds the notice, "Volver a la gestión" and the toast after saving.

const isFichaPath = (pathname) => String(pathname || "").startsWith(CRM_PATHS.ficha);

/**
 * { gestionId, editar, clienteIdParam, gestion, returnHref } of the current Ficha URL. gestion is the gestión of
 * GET gestiones/{id} once it is loaded (null before, or when it cannot be loaded: the notice then stays generic).
 * returnHref goes back to Gestiones IPC with ?gestion=<id>, which opens its Gestionar.
 */
export function useFichaGestionContext({ pathname, search, clienteId }) {
  const params = useMemo(
    () => (isFichaPath(pathname) ? readFichaEditParams(search) : { clienteId: null, gestionId: null, editar: false }),
    [pathname, search]
  );
  const { gestionId } = params;
  const [gestion, setGestion] = useState(null);

  useEffect(() => {
    setGestion(null);
    if (!gestionId) return undefined;
    let active = true;
    Promise.resolve(crmPostventaIpcApi.obtenerGestion(gestionId))
      .then((response) => {
        if (active) setGestion(response?.gestion || null);
      })
      .catch(() => {
        // The notice and "Volver a la gestión" work without the gestión's data.
      });
    return () => {
      active = false;
    };
  }, [gestionId]);

  return {
    gestionId,
    editar: params.editar,
    clienteIdParam: params.clienteId,
    gestion,
    returnHref: gestionId ? gestionHref({ clienteId: clienteId || params.clienteId, gestionId }) : null,
  };
}

// The comercio is named with its nombre de fantasía, as in the queue (09-verificacion-diseno.md §7).
const comercioOf = (context, header) => context?.gestion?.nombreComercio || header?.nombreDeFantasia || header?.razonSocial || "";

// "Estás editando los datos por la gestión de Modificación · Datos bancarios de Farmacia Del Centro".
export const fichaGestionNoticeTitle = (context, header) => {
  const gestion = context?.gestion;
  if (!gestion) return "Estás editando los datos por una gestión";
  const tipo = TIPO_LABELS[codeOf(gestion.tipo)] || "Gestión";
  const comercio = comercioOf(context, header);
  return `Estás editando los datos por la gestión de ${tipo} · ${motivoLabel(gestion)}${comercio ? ` de ${comercio}` : ""}`;
};

export function FichaGestionNotice({ context, header, onNavigate }) {
  if (!context?.gestionId) return null;
  const href = context.returnHref;
  return (
    <Alert variant="info" role="status" className="crm-postventa-ficha-gestion-alert">
      <b>{fichaGestionNoticeTitle(context, header)}</b>
      <div className="mt-2">
        <a
          className="btn btn-outline-secondary btn-sm"
          href={href}
          onClick={(event) => {
            if (!onNavigate || !isPlainClick(event)) return;
            event.preventDefault();
            onNavigate(href);
          }}
        >
          Volver a la gestión
        </a>
      </div>
    </Alert>
  );
}

// Toast after saving the Ficha of a gestión: "Datos actualizados" with "Volver a la gestión". It does not close by
// itself, so the asesor does not lose the action (09-verificacion-diseno.md §7).
export const fichaGestionSavedToast = (context, header, warnings = []) => {
  const terminal = header?.nDeTerminal ? `Terminal ${header.nDeTerminal}` : "";
  const extra = Array.isArray(warnings) && warnings.length ? ` ${warnings.join(" ")}` : "";
  return {
    toast: true,
    position: "bottom-end",
    icon: "success",
    title: "Datos actualizados",
    text: `${[comercioOf(context, header), terminal].filter(Boolean).join(" · ")}${extra}`,
    showConfirmButton: true,
    confirmButtonText: "Volver a la gestión",
    showCloseButton: true,
  };
};
